import React, { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  Eye,
  ShoppingCart,
  ShieldCheck,
  ChevronRight,
  MapPin,
  Phone,
  User,
  X,
  CheckCircle,
  FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import QuotationModal from "./QuotationModal";

/* ==========================================================================
   1. FULLY RESPONSIVE CHECKOUT MODAL
   ========================================================================== */
const CheckoutForm = ({ onClose }) => {
  const existingCartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
  const initialFormData = {
    name: existingCartItems[0]?.name || "",
    mobile: existingCartItems[0]?.mobile || "",
    address: existingCartItems[0]?.address || "",
    pincode: existingCartItems[0]?.pincode || "",
    selectedLocation: existingCartItems[0]?.selectedLocation || "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [pincodeData, setPincodeData] = useState([]);
  const businessPhoneNumber = "+91 94279 86568";

  useEffect(() => {
    document.title = "Checkout - Complete Your Order | NaturalX";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Complete your order with our secure checkout process. Enter your details and proceed with WhatsApp order confirmation.",
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Complete your order with our secure checkout process. Enter your details and proceed with WhatsApp order confirmation.";
      document.head.appendChild(meta);
    }

    const firstInput = document.querySelector('input[name="name"]');
    if (firstInput) {
      firstInput.focus();
    }

    return () => {
      document.title = "NaturalX";
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleWhatsAppClick = () => {
    const cartItemsDetails = existingCartItems
      .map((item, index) => {
        return `(${index + 1}) Product: ${item.productName}, Pattern: ${
          item.productsPattern || "Standard"
        }, Rate: ₹${item.rate}, Area: ${item.sqft} sq ft, Apply Area: ${
          item.Area
        }, Discount: ${item.discount}%, Effective Rate: ₹${
          item.effectiveRate
        }, Subtotal: ₹${item.amount}, Total: ₹${parseFloat(
          item.amountWithIgst || 0,
        ).toFixed(2)} (incl. taxes)`;
      })
      .join("\n");

    const total = existingCartItems.reduce(
      (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
      0,
    );

    const whatsappMessage = `Hello, I would like to proceed with my order.\n\nOrder Details:\n${cartItemsDetails}\n\nCustomer Details:\nName: ${
      formData.name
    }\nMobile: ${formData.mobile}\nAddress: ${formData.address}\nPincode: ${
      formData.pincode
    }\nLocation: ${formData.selectedLocation}\n\nTotal Amount: ₹${total.toFixed(
      2,
    )}`;

    const whatsappUrl = `https://api.whatsapp.com/send?phone=${businessPhoneNumber}&text=${encodeURIComponent(
      whatsappMessage,
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  const fetchPincodeData = async (pincode) => {
    if (pincode.length === 6) {
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${pincode}`,
        );
        const data = await response.json();
        if (data[0].Status === "Success") {
          setPincodeData(data[0].PostOffice);
        } else {
          setPincodeData([]);
        }
      } catch (error) {
        console.error("Error fetching pincode data:", error);
        setPincodeData([]);
      }
    } else {
      setPincodeData([]);
    }
  };

  useEffect(() => {
    if (formData.pincode.length === 6) {
      fetchPincodeData(formData.pincode);
    }
  }, [formData.pincode]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      valid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
      valid = false;
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
      valid = false;
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile number must be 10 digits";
      valid = false;
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
      valid = false;
    } else if (formData.address.trim().length < 10) {
      newErrors.address = "Please provide a complete address";
      valid = false;
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
      valid = false;
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Pincode must be 6 digits";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);
      try {
        const cartItemsDetails = existingCartItems
          .map((item, index) => {
            return `(${index + 1}) Product: ${item.productName}, Pattern: ${
              item.productsPattern || "Standard"
            }, Rate: ₹${item.rate}, Area: ${item.sqft} sq ft, Apply Area: ${
              item.Area
            }, Discount: ${item.discount}%, Effective Rate: ₹${
              item.effectiveRate
            }, Subtotal: ₹${item.amount}, Total: ₹${parseFloat(
              item.amountWithIgst || 0,
            ).toFixed(2)} (incl. taxes)`;
          })
          .join("\n");

        const total = existingCartItems.reduce(
          (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
          0,
        );

        const message = `New order from ${formData.name}. Address: ${
          formData.address
        }, Pincode: ${formData.pincode}, Location: ${
          formData.selectedLocation
        }\n\nOrder Details:\n${cartItemsDetails}\n\nTotal Amount: ₹${total.toFixed(
          2,
        )}`;

        const response = await fetch(
          `https://webadmin.rigve.com/api/save_contact?name=${encodeURIComponent(
            formData.name,
          )}&phone=${encodeURIComponent(
            formData.mobile,
          )}&email=${encodeURIComponent("-")}&msg=${encodeURIComponent(
            message,
          )}`,
        );

        if (!response.ok) {
          throw new Error("Failed to save contact");
        }

        setSubmitSuccess(true);

        setTimeout(() => {
          handleWhatsAppClick();
          onClose();
          localStorage.removeItem("cartItems");
          window.location.reload();
        }, 1500);
      } catch (error) {
        console.error("Error saving contact:", error);
        setErrors({ submit: "Failed to submit order. Please try again." });
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      role="dialog"
      aria-labelledby="checkout-title"
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-slate-100 my-auto max-h-[92vh] flex flex-col">
        {/* Modal Header */}
        <div className="px-5 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-orange-500 to-amber-500 text-white flex items-center justify-between flex-shrink-0">
          <div>
            <h2
              id="checkout-title"
              className="text-lg sm:text-xl font-bold tracking-tight"
            >
              Complete Your Order
            </h2>
            <p className="text-xs text-orange-100 mt-0.5">
              Confirm details to proceed with WhatsApp verification
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition focus:outline-none"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          {submitSuccess ? (
            <div className="text-center py-8 sm:py-10 space-y-3">
              <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                Order Submitted!
              </h3>
              <p className="text-sm text-slate-500">
                Redirecting to WhatsApp...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Full Name *
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 transition ${
                      errors.name ? "border-red-500" : "border-slate-200"
                    }`}
                    placeholder="John Doe"
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              {/* Mobile Field */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Mobile Number *
                </label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
                  <input
                    type="tel"
                    name="mobile"
                    maxLength="10"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 transition ${
                      errors.mobile ? "border-red-500" : "border-slate-200"
                    }`}
                    placeholder="10-digit phone number"
                  />
                </div>
                {errors.mobile && (
                  <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
                )}
              </div>

              {/* Address Field */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Address *
                </label>
                <textarea
                  name="address"
                  rows="3"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`w-full p-3 text-sm bg-slate-50 border rounded-xl outline-none resize-none focus:bg-white focus:ring-2 focus:ring-orange-500 transition ${
                    errors.address ? "border-red-500" : "border-slate-200"
                  }`}
                  placeholder="Complete home or site address..."
                />
                {errors.address && (
                  <p className="text-red-500 text-xs mt-1">{errors.address}</p>
                )}
              </div>

              {/* Pincode & Location */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Pincode *
                  </label>
                  <div className="relative">
                    <MapPin className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
                    <input
                      type="text"
                      name="pincode"
                      maxLength="6"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 transition ${
                        errors.pincode ? "border-red-500" : "border-slate-200"
                      }`}
                      placeholder="6-digit pincode"
                    />
                  </div>
                  {errors.pincode && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.pincode}
                    </p>
                  )}
                </div>

                {formData.pincode.length === 6 && pincodeData.length > 0 && (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                      Location
                    </label>
                    <select
                      name="selectedLocation"
                      value={formData.selectedLocation}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-orange-500 transition"
                    >
                      <option value="">Select location</option>
                      {pincodeData.map((loc, idx) => (
                        <option key={idx} value={loc.Name}>
                          {loc.Name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {errors.submit && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
                  {errors.submit}
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-2 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-1/3 py-3 rounded-xl border border-slate-200 text-slate-600 font-medium text-sm hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full sm:w-2/3 py-3 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold text-sm transition shadow-lg shadow-orange-600/20 disabled:opacity-50"
                >
                  {isLoading ? "Processing..." : "Submit Order"}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 sm:px-6 py-3 bg-slate-50 border-t border-slate-100 text-center text-xs text-slate-400 flex-shrink-0">
          🔒 Your information is secure and used only for order processing
        </div>
      </div>
    </div>
  );
};

/* ==========================================================================
   2. MAIN CART PAGE COMPONENT (RESPONSIVE)
   ========================================================================== */
const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showQuotationModal, setShowQuotationModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Shopping Cart - Your Interior Wall Textures Store";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Review your selected interior wall texture products. Secure checkout with competitive pricing and fast delivery.",
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Review your selected interior wall texture products. Secure checkout with competitive pricing and fast delivery.";
      document.head.appendChild(meta);
    }

    try {
      const existingCartItems =
        JSON.parse(localStorage.getItem("cartItems")) || [];
      setCartItems(existingCartItems);

      const total = existingCartItems.reduce(
        (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
        0,
      );
      setTotalAmount(total);
    } catch (error) {
      console.error("Error loading cart items:", error);
      setCartItems([]);
      setTotalAmount(0);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (showCheckout || showQuotationModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showCheckout, showQuotationModal]);

  const handleRemoveItem = async (index) => {
    if (isRemoving) return;
    setIsRemoving(true);

    try {
      const updatedCartItems = [...cartItems];
      updatedCartItems.splice(index, 1);
      localStorage.setItem("cartItems", JSON.stringify(updatedCartItems));
      setCartItems(updatedCartItems);

      const total = updatedCartItems.reduce(
        (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
        0,
      );
      setTotalAmount(total);

      await new Promise((resolve) => setTimeout(resolve, 500));
      window.location.reload();
    } catch (error) {
      console.error("Error removing item from cart:", error);
      alert("Failed to remove item from cart. Please try again.");
      setIsRemoving(false);
    }
  };

  const handleAddMoreProducts = () => {
    navigate("/Products/5/Interior%20Wall%20Textures");
  };

  const handleProceedToCheckout = () => {
    if (cartItems.length === 0) {
      alert("Your cart is empty. Please add products before checkout.");
      return;
    }
    setShowCheckout(true);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ShoppingCart",
    name: "Shopping Cart",
    description: "Interior wall texture products cart",
    offers: cartItems.map((item) => ({
      "@type": "Offer",
      name: item.productName,
      price: item.amountWithIgst,
      priceCurrency: "INR",
    })),
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <main className="min-h-screen bg-slate-50/60 pb-24 lg:pb-12 pt-6 sm:pt-10">
        <div className="container mx-auto max-w-6xl px-3 sm:px-6">
          {/* Top Header */}
          <header className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-200/80 gap-3">
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <h1 className="text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
                  Shopping Cart
                </h1>
                <span className="px-2.5 py-0.5 text-xs font-semibold bg-orange-100 text-orange-700 rounded-full">
                  {cartItems.length} {cartItems.length === 1 ? "Item" : "Items"}
                </span>
              </div>
              {/* <p className="text-xs sm:text-sm text-slate-500 mt-1">
                Review your selected wall textures & custom sq ft calculations
              </p> */}
            </div>

            <button
              onClick={handleAddMoreProducts}
              className="inline-flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100 px-3.5 py-2.5 rounded-xl transition w-full sm:w-auto"
            >
              <Plus className="w-4 h-4" /> Add More Textures
            </button>
          </header>

          {cartItems.length === 0 ? (
            /* Empty State */
            <section className="bg-white border border-slate-200/80 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center max-w-md mx-auto shadow-sm my-6">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-orange-50 text-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <ShoppingCart className="w-8 h-8 sm:w-10 sm:h-10" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                Your cart is empty
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 mb-6 leading-relaxed">
                Looks like you haven't added any interior wall textures yet.
                Explore our modern collection and transform your walls.
              </p>
              <button
                onClick={handleAddMoreProducts}
                className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white font-medium text-sm rounded-xl transition shadow-lg shadow-orange-600/20"
              >
                Browse Products
              </button>
            </section>
          ) : (
            /* Active Cart Grid */
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
              {/* Product List */}
              <section className="lg:col-span-8 space-y-3 sm:space-y-4">
                {cartItems.map((item, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl border border-slate-200/80 p-3.5 sm:p-5 shadow-sm hover:shadow-md transition-all duration-300 relative group overflow-hidden"
                  >
                    <div className="flex flex-col sm:flex-row gap-3.5 sm:gap-5 items-stretch">
                      {/* Thumbnail & Badges */}
                      <div className="w-full sm:w-36 h-40 sm:h-36 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 relative">
                        <img
                          src={item.mainImage}
                          alt={item.productName}
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                        />
                        {item.discount > 0 && (
                          <span className="absolute top-2 left-2 bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md sm:hidden">
                            {item.discount}% OFF
                          </span>
                        )}
                      </div>

                      {/* Content Info */}
                      <div className="flex-1 flex flex-col justify-between space-y-3">
                        {/* Title & Delete button */}
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <h3 className="font-bold text-slate-900 text-base sm:text-lg capitalize leading-snug">
                              {item.productName}
                            </h3>
                            <p className="text-xs font-medium text-slate-400 mt-0.5">
                              Apply Area:{" "}
                              <span className="text-slate-600">
                                {item.Area}
                              </span>
                            </p>
                          </div>

                          <button
                            onClick={() => handleRemoveItem(index)}
                            className="p-2 rounded-xl text-slate-400 hover:text-red-500 hover:bg-red-50 transition flex-shrink-0"
                            title="Remove Item"
                          >
                            {isRemoving ? (
                              <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>

                        {/* Details Specs Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                          <div>
                            <span className="text-slate-400 text-[10px] uppercase font-semibold block">
                              Pattern
                            </span>
                            <span className="font-semibold text-slate-700 truncate block">
                              {item.productsPattern || "Standard"}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-400 text-[10px] uppercase font-semibold block">
                              Coverage
                            </span>
                            <span className="font-semibold text-slate-700 block">
                              {item.sqft} sq ft
                            </span>
                          </div>
                          <div className="col-span-2 sm:col-span-1">
                            <span className="text-slate-400 text-[10px] uppercase font-semibold block">
                              Effective Rate
                            </span>
                            <span className="font-semibold text-slate-700 block">
                              ₹{item.effectiveRate}/sq ft
                            </span>
                          </div>
                        </div>

                        {/* Price Bar & Detail View Link */}
                        <div className="flex items-center justify-between pt-1 border-t border-slate-100/60 sm:border-0">
                          <a
                            href={item.pageUrl}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 hover:underline"
                          >
                            <Eye className="w-3.5 h-3.5" /> View Details
                          </a>

                          <div className="text-right">
                            <span className="text-base sm:text-lg font-bold text-slate-900">
                              ₹{parseFloat(item.amountWithIgst || 0).toFixed(2)}
                            </span>
                            <span className="text-[10px] text-slate-400 block -mt-1">
                              (incl. taxes)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </section>

              {/* Order Summary (Desktop Sticky Sidebar) */}
              <aside className="lg:col-span-4">
                <div className="bg-white border border-slate-200/80 rounded-2xl p-5 sm:p-6 shadow-sm sticky top-6 space-y-5">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3.5">
                    <h2 className="text-base font-bold text-slate-900">
                      Order Summary
                    </h2>
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                      <ShieldCheck className="w-3.5 h-3.5" /> Verified
                    </span>
                  </div>

                  {/* Pricing Breakdown */}
                  <div className="space-y-2.5 text-xs sm:text-sm text-slate-600">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span className="font-medium text-slate-800">
                        ₹{totalAmount.toFixed(2)}
                      </span>
                    </div>

                    <div className="flex justify-between">
                      <span>GST & Taxes</span>
                      <span className="text-emerald-600 font-medium">
                        Included
                      </span>
                    </div>

                    <div className="border-t border-slate-100 pt-3 flex justify-between items-baseline font-bold text-slate-900 text-base sm:text-lg">
                      <span>Total Amount</span>
                      <span className="text-orange-600">
                        ₹{totalAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Primary Buttons */}
                  <div className="space-y-2.5 pt-1">
                    <button
                      onClick={handleProceedToCheckout}
                      className="w-full py-3 sm:py-3.5 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl text-sm flex items-center justify-center gap-2 transition shadow-lg shadow-orange-600/20"
                    >
                      Proceed to Checkout <ChevronRight className="w-4 h-4" />
                    </button>

                    <button
                      onClick={() => setShowQuotationModal(true)}
                      className="w-full py-2.5 sm:py-3 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1.5 transition"
                    >
                      <FileText className="w-4 h-4 text-orange-600" /> Download
                      Quotation
                    </button>
                  </div>

                  {/* Order Notice */}
                  <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 text-center">
                    <p className="text-[11px] text-slate-400 leading-normal">
                      🔒 Orders are processed via WhatsApp for instant site
                      verification & custom measurements.
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>

        {/* Floating Mobile Bottom Sticky Action Bar */}
        {cartItems.length > 0 && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-slate-200/80 p-3.5 z-40 shadow-2xl flex items-center justify-between gap-3">
            <div>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold block">
                Total Amount
              </span>
              <span className="text-lg font-bold text-orange-600">
                ₹{totalAmount.toFixed(2)}
              </span>
            </div>
            <button
              onClick={handleProceedToCheckout}
              className="px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl text-sm flex items-center gap-1.5 transition shadow-lg shadow-orange-600/20"
            >
              Checkout <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Modal Mount Points */}
        {showCheckout && (
          <CheckoutForm onClose={() => setShowCheckout(false)} />
        )}
        {showQuotationModal && (
          <QuotationModal onClose={() => setShowQuotationModal(false)} />
        )}
      </main>
    </>
  );
};

export default CartPage;
